import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getDatabase, ref, get } from 'firebase/database';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Edit3, Trash2, ChevronDown, ChevronRight, Info, MoreVertical, Download } from 'lucide-react';
import { BreadcrumbComponent } from '../../../Components/BreadCrumb';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import './MaintenanceActive.css';
import { ComponentLifetime, TemplatesByType } from '../TemplatesEditor/types';
import { Building } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { BaseType } from '../../../types/Enums';
import ComponentDetailPane from '../../BuildingDetails/components/ComponentDetailPane';
import { translate } from '../../../types/EnumTranslations';
import { getChartColor } from '../../../Helpers/chartColors';
import { BASE_TYPE_GROUPS, presentBaseTypeGroupLabel } from '../../../Helpers/baseTypeUtils';
import { auth } from '../../../firebase';
import { useUsersData } from '../../../Helpers/UsersContext';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import {
    generateYearlyPlansForBuilding,
    YearlyActivity,
    ActivityType,
    CostSource,
    COST_SOURCE_LABELS
} from '../../../Helpers/maintenanceCostUtils';
import type { MaintenanceActivePlan } from '../types';
import { CombinedTimeline, ChartTimelineLegend } from '../MaintenanceDraft/components/ui/CombinedTimeline';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { CostSourceChip, CostSourceLegend } from '../components/CostSourceChip';
import { getInvalidComponents } from '../MaintenanceDraft/utils/componentValidation';
import { getInvalidConfigTypes } from '../MaintenanceDraft/utils/configValidation';
import { exportMaintenancePlanToPDF, exportMaintenancePlanToCSV } from '../../../Helpers/maintenanceExportUtils';
import { Modal } from 'react-bootstrap';
import { useMediaQuery } from '../../../hooks/useIsSmallScreen';
import { createMaintenanceDraft, DraftStart } from '../../../api/maintenanceDraftApi';
import { maintenancePlanApi } from '../../../api/maintenancePlanApi';
import { CreateDraftModal } from '../components/CreateDraftModal';
import { applyComponentPatch, ComponentFieldPatch } from '../../../Helpers/componentPatch';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

// Activity details interface matching DraftPreviewContent
interface ActivityWithDetails {
    componentKey: string;
    componentName: string;
    activityType: ActivityType;
    activityName: string;
    cost: number;
    costCalculation: string;
    costSource?: CostSource;
    costComment?: string;
    hasCostAttachments?: boolean;
    investmentPercentage?: number;
    comment?: string;
}

interface BaseTypeGroup {
    baseType: string;
    label: string;
    totalCost: number;
    activities: ActivityWithDetails[];
}

interface YearData {
    year: number;
    totalCost: number;
    hasActivities: boolean;
    baseTypeGroups: BaseTypeGroup[];
}

const MaintenanceActive: React.FC = () => {
    const { buildingKey } = useParams<{ buildingKey: string }>();
    const navigate = useNavigate();
    const { types: buildComponentTypes, getLifetime, getTemplates, getTranslation } = useBuildComponentTypes();
    const [user] = useAuthState(auth);
    const { users } = useUsersData();
    const [building, setBuilding] = useState<Building | null>(null);

    // Set document title
    useDocumentTitle(`Underhållsplan - ${building?.summary?.name || 'Byggnad'}`);
    const [activePlan, setActivePlan] = useState<MaintenanceActivePlan | null>(null);
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [maintenanceTemplates, setMaintenanceTemplates] = useState<TemplatesByType>({});
    const [componentConditions, setComponentConditions] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [yearsToShow, setYearsToShow] = useState<number>(10);
    const [yearlyPlans, setYearlyPlans] = useState<YearlyActivity[]>([]);
    const [hasDraft, setHasDraft] = useState<boolean>(false);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [creatingDraft, setCreatingDraft] = useState<boolean>(false);
    const [createDraftError, setCreateDraftError] = useState<string | null>(null);
    const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [expandedYears, setExpandedYears] = useState<Set<number>>(() => new Set());
    const [tooltip, setTooltip] = useState<{
        content: React.ReactNode;
        x: number;
        y: number;
    } | null>(null);
    const [costTypeFilter, setCostTypeFilter] = useState<'all' | 'maintenance' | 'replacement'>('all');
    const [showActionsMenu, setShowActionsMenu] = useState<boolean>(false);
    const [selectedComponentKey, setSelectedComponentKey] = useState<string | null>(null);
    // The base-type group a chart segment click landed on; the year list scrolls to it and flashes it
    const [highlightedGroup, setHighlightedGroup] = useState<{ year: number; baseTypes: readonly string[] } | null>(null);
    const chartRef = useRef<ChartJS<'bar'> | null>(null);

    // Below 1200px the detail sidebar is hidden (MaintenanceActive.css), so a
    // selected component opens in a modal instead.
    const sidebarHidden = useMediaQuery('(max-width: 1200px)');
    // Hover tooltips stick after a tap and open off-screen on touch devices.
    const canHover = !useMediaQuery('(hover: none)');
    const setHoverTooltip = useCallback((next: typeof tooltip) => {
        if (canHover || next === null) setTooltip(next);
    }, [canHover]);

    const db = getDatabase();

    // Check if current user is admin
    useEffect(() => {
        if (user && users) {
            const currentUser = users.find(u => u.key === user.uid);
            setIsAdmin(currentUser?.admin || currentUser?.superadmin || false);
        }
    }, [user, users]);

    // Toggle year expansion
    const toggleYear = useCallback((year: number) => {
        setExpandedYears(prev => {
            const next = new Set(prev);
            if (next.has(year)) {
                next.delete(year);
            } else {
                next.add(year);
            }
            return next;
        });
    }, []);

    // Handle component click - toggle selection
    const handleComponentClick = useCallback((componentKey: string) => {
        setSelectedComponentKey(prev => prev === componentKey ? null : componentKey);
    }, []);

    // Resolve selected component for detail pane
    // The page keeps its own copy of the building, so edits saved in the
    // component pane are applied to it here
    const handleComponentPatched = useCallback((componentKey: string, patch: ComponentFieldPatch) => {
        setBuilding(prev => applyComponentPatch(prev, componentKey, patch));
    }, []);

    const selectedComponent = useMemo(() => {
        if (!selectedComponentKey || !building?.model.structure?.components) return null;
        const componentData = building.model.structure.components[selectedComponentKey];
        if (!componentData) return null;
        return new BaseComponentImpl(componentData);
    }, [selectedComponentKey, building]);

    // Load data
    const loadData = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);

            const [
                buildingSnapshot,
                summarySnapshot,
                activePlanSnapshot,
                conditionsSnapshot,
                draftSnapshot
            ] = await Promise.all([
                get(ref(db, `buildings/${buildingKey}`)),
                get(ref(db, `buildingsSummary/${buildingKey}`)),
                get(ref(db, `maintenance/active/${buildingKey}`)),
                get(ref(db, `componentConditions/${buildingKey}`)),
                get(ref(db, `maintenance/drafts/${buildingKey}`))
            ]);

            // Load building with summary
            if (buildingSnapshot.exists()) {
                const buildingData = buildingSnapshot.val();
                const summaryData = summarySnapshot.exists() ? summarySnapshot.val() : { key: buildingKey };

                setBuilding({
                    key: buildingKey!,
                    model: buildingData,
                    summary: summaryData
                });
            }

            // Load active plan
            if (activePlanSnapshot.exists()) {
                const data = activePlanSnapshot.val() as MaintenanceActivePlan;
                setActivePlan(data);
            } else {
                setActivePlan(null);
            }

            // Load component lifetimes and templates from context
            const lifetimesByType: Record<string, ComponentLifetime> = {};
            const templatesByType: TemplatesByType = {};
            
            Object.keys(buildComponentTypes).forEach((type: string) => {
                const lifetime = getLifetime(type);
                if (lifetime) {
                    lifetimesByType[type] = lifetime;
                }
                const templates = getTemplates(type);
                templatesByType[type] = templates;
            });
            
            setComponentLifetimes(lifetimesByType);
            setMaintenanceTemplates(templatesByType);

            // Load component conditions
            if (conditionsSnapshot.exists()) {
                const data = conditionsSnapshot.val();
                setComponentConditions(data);
            }

            // Check for draft
            setHasDraft(draftSnapshot.exists());

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    }, [buildingKey, db]);

    // Create new draft
    const handleCreateDraft = (): void => {
        setShowCreateModal(true);
    };

    const handleConfirmCreateDraft = async (start: DraftStart): Promise<void> => {
        if (!building || !buildingKey) return;

        // Check if all components are valid before creating draft
        if (getInvalidComponents(building).length > 0) {
            setShowCreateModal(false);
            navigate(`/building/${buildingKey}/validate-maintenance-components`);
            return;
        }

        // Check config validation after component validation passes
        if (getInvalidConfigTypes(building, getLifetime, getTranslation).length > 0) {
            setShowCreateModal(false);
            navigate(`/building/${buildingKey}/validate-maintenance-config`);
            return;
        }

        setCreatingDraft(true);
        setCreateDraftError(null);
        try {
            await createMaintenanceDraft({ buildingKey, start, building, componentLifetimes, maintenanceTemplates });
            setShowCreateModal(false);
            navigate(`/maintenanceDrafts/${buildingKey}`);
        } catch (error) {
            setCreateDraftError(error instanceof Error ? error.message : 'Utkastet kunde inte skapas. Försök igen.');
        } finally {
            setCreatingDraft(false);
        }
    };

    const handleCancelCreateDraft = (): void => {
        setShowCreateModal(false);
        setCreateDraftError(null);
    };

    const handleEditDraft = (): void => {
        navigate(`/maintenanceDrafts/${buildingKey}`);
    };

    // Export handlers
    const handleExportPDF = (): void => {
        setShowActionsMenu(false);
        exportMaintenancePlanToPDF(
            yearlyPlans,
            building?.summary?.name || 'Underhållsplan',
            yearsToShow,
            costTypeFilter,
            getTranslation
        );
    };

    const handleExportCSV = (): void => {
        setShowActionsMenu(false);
        exportMaintenancePlanToCSV(
            yearlyPlans,
            building?.summary?.name || 'Underhållsplan',
            yearsToShow,
            costTypeFilter,
            getTranslation
        );
    };

    // Archive plan handlers
    const handleArchivePlan = (): void => {
        setShowArchiveModal(true);
    };

    const handleConfirmArchive = async (): Promise<void> => {
        if (!activePlan || !buildingKey || !user) return;

        try {
            // Atomically move the plan to the archive (admin only)
            await maintenancePlanApi.archivePlan(buildingKey);

            setShowArchiveModal(false);
            // Reload data to reflect changes
            await loadData();
        } catch (error) {
            console.error('Error archiving plan:', error);
        }
    };

    const handleCancelArchive = (): void => {
        setShowArchiveModal(false);
    };

    // Generate yearly plans using utility function
    const generateYearlyPlans = useCallback((): void => {
        if (!building || !activePlan || !componentLifetimes || !maintenanceTemplates) {
            setYearlyPlans([]);
            return;
        }

        const plans = generateYearlyPlansForBuilding({
            building,
            plan: activePlan,
            componentLifetimes,
            maintenanceTemplates,
            componentConditions,
            yearsToShow,
            buildingName: building.summary?.name,
            buildComponentTypes,
            includeEmptyYears: true
        });

        setYearlyPlans(plans);
    }, [building, activePlan, componentLifetimes, maintenanceTemplates, componentConditions, yearsToShow]);

    // Helper to calculate cost based on filter and investment percentage
    const getFilteredCost = useCallback((cost: number, investmentPercentage?: number): number => {
        const pct = investmentPercentage ?? 0;
        if (costTypeFilter === 'all') return cost;
        if (costTypeFilter === 'replacement') return cost * pct / 100; // Investering
        return cost * (100 - pct) / 100; // Underhåll
    }, [costTypeFilter]);

    // Transform yearlyPlans to YearData format for new display style
    const { yearsData, grandTotal, firstYearWithActivities } = useMemo(() => {
        if (yearlyPlans.length === 0) {
            return { yearsData: [], grandTotal: 0, firstYearWithActivities: null };
        }

        let total = 0;
        let firstWithActivities: number | null = null;

        const data: YearData[] = yearlyPlans.map(yearPlan => {
            // Pre-compute baseType groups with filtered costs
            const groups: Record<string, BaseTypeGroup> = {};
            let yearTotal = 0;

            yearPlan.activities.forEach(activity => {
                const filteredCost = getFilteredCost(activity.cost, activity.investmentPercentage);
                yearTotal += filteredCost;

                const baseType = activity.baseType;
                if (!groups[baseType]) {
                    groups[baseType] = {
                        baseType,
                        label: translate(baseType as BaseType),
                        totalCost: 0,
                        activities: []
                    };
                }
                groups[baseType].totalCost += filteredCost;
                groups[baseType].activities.push({
                    componentKey: activity.componentKey,
                    componentName: activity.componentName,
                    activityType: activity.activityType,
                    activityName: activity.activityName,
                    cost: filteredCost,
                    costCalculation: activity.costCalculation,
                    costSource: activity.costSource,
                    costComment: activity.costComment,
                    hasCostAttachments: activity.hasCostAttachments,
                    investmentPercentage: activity.investmentPercentage,
                    comment: activity.comment
                });
            });

            total += yearTotal;

            const hasActivities = yearPlan.activities.length > 0;
            if (hasActivities && firstWithActivities === null) {
                firstWithActivities = yearPlan.year;
            }

            return {
                year: yearPlan.year,
                totalCost: yearTotal,
                hasActivities,
                baseTypeGroups: Object.values(groups).sort((a, b) => a.label.localeCompare(b.label, 'sv'))
            };
        });

        return { yearsData: data, grandTotal: total, firstYearWithActivities: firstWithActivities };
    }, [yearlyPlans, getFilteredCost]);

    // Build chart datasets - stacked by component type, in BASE_TYPE_GROUPS canonical order
    const chartDatasets = useMemo(() => {
        // Collect all unique baseTypes that have activities
        const activeBaseTypes = new Set<string>();
        yearsData.forEach(yearData => {
            yearData.baseTypeGroups.forEach(group => {
                if (group.totalCost > 0) {
                    activeBaseTypes.add(group.baseType);
                }
            });
        });

        // Create one dataset per BASE_TYPE_GROUPS entry, in canonical order
        return BASE_TYPE_GROUPS
            .map((group, index) => {
                const activeGroupBaseTypes = group.baseTypes.filter(bt => activeBaseTypes.has(bt));
                if (activeGroupBaseTypes.length === 0) return null;

                return {
                    label: presentBaseTypeGroupLabel(group, bt => activeBaseTypes.has(bt)),
                    baseTypes: group.baseTypes as readonly string[],
                    data: yearsData.map(yearData =>
                        yearData.baseTypeGroups
                            .filter(g => (group.baseTypes as string[]).includes(g.baseType))
                            .reduce((sum, g) => sum + g.totalCost, 0)
                    ),
                    backgroundColor: getChartColor(index),
                    borderRadius: 0,
                    borderSkipped: false as const,
                };
            })
            .filter((d): d is NonNullable<typeof d> => d !== null);
    }, [yearsData]);

    // A click on a year's base types in the chart or the timeline opens that year alone in
    // the list (collapsing the others keeps the scroll short) and scrolls to the group.
    // A group with a single component selects it too.
    const focusYearGroup = useCallback((year: number, baseTypes: readonly string[]) => {
        const yearData = yearsData.find(y => y.year === year);
        if (!yearData) return;

        setExpandedYears(new Set([year]));
        setHighlightedGroup({ year, baseTypes });

        const componentKeys = new Set(
            yearData.baseTypeGroups
                .filter(g => baseTypes.includes(g.baseType))
                .flatMap(g => g.activities.map(a => a.componentKey))
        );
        if (componentKeys.size === 1) {
            setSelectedComponentKey([...componentKeys][0]);
        }
    }, [yearsData]);

    // Chart bar segment = one year × one base-type group
    const handleChartClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
        const chart = chartRef.current;
        if (!chart) return;
        const [element] = getElementAtEvent(chart, event);
        if (!element) return;
        const yearData = yearsData[element.index];
        const dataset = chartDatasets[element.datasetIndex];
        if (yearData && dataset) focusYearGroup(yearData.year, dataset.baseTypes);
    }, [yearsData, chartDatasets, focusYearGroup]);

    // Once the clicked year has rendered expanded, scroll its first matching group into view
    useEffect(() => {
        if (!highlightedGroup) return;
        const target = document.querySelector<HTMLElement>(
            `[data-year="${highlightedGroup.year}"] .basetype-group.highlighted`
        );
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const timer = window.setTimeout(() => setHighlightedGroup(null), 1500);
        return () => window.clearTimeout(timer);
    }, [highlightedGroup]);

    // Set initial expanded year
    useEffect(() => {
        if (firstYearWithActivities !== null && expandedYears.size === 0) {
            setExpandedYears(new Set([firstYearWithActivities]));
        }
    }, [firstYearWithActivities]);

    useEffect(() => {
        if (buildingKey) {
            loadData();
        }
    }, [buildingKey, loadData]);

    useEffect(() => {
        if (!loading) {
            generateYearlyPlans();
        }
    }, [loading, generateYearlyPlans, buildComponentTypes]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <p className="text-muted">Laddar underhållsplan...</p>
                </div>
            </div>
        );
    }

    if (!building) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnad hittades inte</h4>
                <p>Den angivna byggnaden kunde inte hittas.</p>
            </div>
        );
    }

    if (!activePlan) {
        const buildingName = building?.summary?.name || 'Namnlös byggnad';

        return (
            <div className="maintenance-active">
                {/* Header Row: Breadcrumbs */}
                <div className="maintenance-active-header">
                    <BreadcrumbComponent
                        items={[
                            { text: buildingName, link: `/building/${buildingKey}` },
                            { text: 'Underhåll' }
                        ]}
                    />
                </div>

                {/* Empty State */}
                <div
                    className="maintenance-active-empty-container container"
                    style={{ marginLeft: '20px', marginRight: 0, paddingLeft: 0, paddingRight: 0 }}
                >
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <FileText size={48} className="text-muted mb-3" />
                            <h5>Ingen aktiv underhållsplan</h5>
                            <p className="text-muted">
                                Det finns ingen aktiv underhållsplan för denna byggnad.
                                {hasDraft
                                    ? ' Du har ett pågående utkast som du kan fortsätta arbeta med.'
                                    : ' Skapa ett nytt utkast för att komma igång med underhållsplaneringen.'
                                }
                            </p>
                            {hasDraft ? (
                                <button className="btn btn-primary" onClick={handleEditDraft}>
                                    <Edit3 size={16} className="me-1" />
                                    Redigera utkast
                                </button>
                            ) : (
                                <button className="btn btn-primary" onClick={handleCreateDraft}>
                                    <FileText size={16} className="me-1" />
                                    Skapa nytt utkast
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Create Draft Modal */}
                <CreateDraftModal
                    show={showCreateModal}
                    hasActivePlan={false}
                    creating={creatingDraft}
                    error={createDraftError}
                    onConfirm={handleConfirmCreateDraft}
                    onCancel={handleCancelCreateDraft}
                />
            </div>
        );
    }

    const buildingName = building.summary?.name || 'Namnlös byggnad';

    return (
        <div className="maintenance-active">
            {/* Header Row: Breadcrumbs */}
            <div className="maintenance-active-header">
                <BreadcrumbComponent
                    items={[
                        { text: buildingName, link: `/building/${buildingKey}` },
                        { text: 'Underhåll' }
                    ]}
                />
            </div>

            {/* Two-pane layout */}
            <div className="maintenance-active-layout">
            {/* Plan Container */}
            <div className="maintenance-active-plan-container dropshadow">
                {/* Controls and Summary Header */}
                <div className="ma-plan-header">
                    <div className="ma-plan-header-left">
                        <h3 className="ma-plan-title">Underhållsplan</h3>
                        {activePlan.metadata?.activatedAt && (
                            <p className="ma-plan-subtitle">
                                Aktiverad {new Date(activePlan.metadata.activatedAt).toLocaleDateString('sv-SE')} av {(() => {
                                    const activatedByUid = (activePlan.metadata as any).activatedBy;
                                    const activatedByUser = users?.find(u => u.key === activatedByUid);
                                    return activatedByUser?.name || 'okänd';
                                })()}
                            </p>
                        )}
                    </div>
                    <div className="ma-plan-header-center">
                        <div className="filter-group">
                            <label htmlFor="yearsSelect" className="filter-label">Visa:</label>
                            <select
                                id="yearsSelect"
                                className="form-select form-select-sm filter-select"
                                value={yearsToShow}
                                onChange={(e) => setYearsToShow(parseInt(e.target.value, 10))}
                            >
                                {[10, 20, 30, 40, 50].map(years => (
                                    <option key={years} value={years}>{years} år</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label htmlFor="costTypeSelect" className="filter-label">Typ:</label>
                            <select
                                id="costTypeSelect"
                                className="form-select form-select-sm filter-select"
                                value={costTypeFilter}
                                onChange={(e) => setCostTypeFilter(e.target.value as 'all' | 'maintenance' | 'replacement')}
                            >
                                <option value="all">Alla</option>
                                <option value="maintenance">Underhåll</option>
                                <option value="replacement">Investering</option>
                            </select>
                        </div>
                    </div>
                    <div className="ma-plan-header-right">
                        <div className="ma-total-cost-box">
                            <span className="ma-total-cost-label">Totala kostnader</span>
                            <span className="ma-total-cost-value">{grandTotal.toLocaleString('sv-SE')} kr</span>
                        </div>
                        <div className="actions-menu-container">
                            <button
                                className="btn btn-link actions-menu-trigger"
                                onClick={() => setShowActionsMenu(!showActionsMenu)}
                                onBlur={() => setTimeout(() => setShowActionsMenu(false), 150)}
                            >
                                <MoreVertical size={20} />
                            </button>
                            {showActionsMenu && (
                                <div className="actions-menu-dropdown">
                                    {hasDraft ? (
                                        <button
                                            className="dropdown-item"
                                            onClick={() => { setShowActionsMenu(false); handleEditDraft(); }}
                                        >
                                            <Edit3 size={16} className="me-2" />
                                            Redigera utkast
                                        </button>
                                    ) : (
                                        <button
                                            className="dropdown-item"
                                            onClick={() => { setShowActionsMenu(false); handleCreateDraft(); }}
                                        >
                                            <FileText size={16} className="me-2" />
                                            Nytt utkast
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button
                                            className="dropdown-item text-danger"
                                            onClick={() => { setShowActionsMenu(false); handleArchivePlan(); }}
                                        >
                                            <Trash2 size={16} className="me-2" />
                                            Arkivera plan
                                        </button>
                                    )}
                                    <div className="dropdown-divider" />
                                    <button
                                        className="dropdown-item"
                                        onClick={handleExportPDF}
                                        disabled={yearlyPlans.length === 0}
                                    >
                                        <FileText size={16} className="me-2" />
                                        Exportera PDF
                                    </button>
                                    <button
                                        className="dropdown-item"
                                        onClick={handleExportCSV}
                                        disabled={yearlyPlans.length === 0}
                                    >
                                        <Download size={16} className="me-2" />
                                        Exportera CSV
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="draft-preview-content" style={{ padding: '30px 0 16px 0' }}>
                    {/* Combined Chart and Timeline Container */}
                    <div className="chart-timeline-container">
                        {/* Cost Chart */}
                        <div className="cost-chart-section">
                            {yearsData.length > 0 && (
                                <Bar
                                    ref={chartRef}
                                    onClick={handleChartClick}
                                    data={{
                                        labels: yearsData.map(y => y.year.toString()),
                                        datasets: chartDatasets
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        onHover: (_event, elements, chart) => {
                                            chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                                        },
                                        animation: {
                                            duration: 500
                                        },
                                        plugins: {
                                            tooltip: {
                                                callbacks: {
                                                    label: (context) => `${context.dataset.label}: ${(context.parsed.y ?? 0).toLocaleString('sv-SE')} kr`
                                                }
                                            },
                                            legend: {
                                                display: false
                                            }
                                        },
                                        scales: {
                                            x: {
                                                stacked: true,
                                                grid: { display: false },
                                                ticks: { font: { size: 14, family: 'Source Sans Pro' } }
                                            },
                                            y: {
                                                stacked: true,
                                                grid: { display: false },
                                                ticks: {
                                                    font: { size: 14, family: 'Source Sans Pro' },
                                                    callback: (value) => {
                                                        const num = Number(value);
                                                        return num >= 1000 ? `${(num / 1000).toFixed(0)}k` : num;
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                    height={250}
                                />
                            )}
                        </div>

                        {/* Combined Timeline */}
                        <CombinedTimeline
                            yearsData={yearsData}
                            setTooltip={setHoverTooltip}
                            onActivityClick={focusYearGroup}
                            hideLegend
                            hideHeader
                        />

                        {/* Shared Legend */}
                        <ChartTimelineLegend yearsData={yearsData} />
                    </div>

                {/* Urgent Maintenance Debt */}
                {(() => {
                    const currentYear = new Date().getFullYear();
                    const currentYearData = yearsData.find(y => y.year === currentYear);
                    const urgentCost = currentYearData?.totalCost || 0;

                    return urgentCost > 0 ? (
                        <div className="urgent-debt-section">
                            <span className="urgent-debt-label">
                                Akut underhållsskuld ({currentYear})
                                <span
                                    className="urgent-debt-info"
                                    onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setHoverTooltip({
                                            content: 'Summan av alla planerade underhålls- och investeringskostnader som förfaller under innevarande kalenderår',
                                            x: rect.right,
                                            y: rect.top + rect.height / 2
                                        });
                                    }}
                                    onMouseLeave={() => setHoverTooltip(null)}
                                >
                                    <Info size={14} />
                                </span>
                            </span>
                            <span className="urgent-debt-value">{urgentCost.toLocaleString('sv-SE')} kr</span>
                        </div>
                    ) : null;
                })()}

                {yearsData.length > 0 && <CostSourceLegend className="mb-2" />}

                {/* Year Sections (Collapsible) */}
                {yearsData.map((yearData) => {
                    const isExpanded = expandedYears.has(yearData.year);

                    return (
                        <div key={yearData.year} className="year-section" data-year={yearData.year}>
                            {/* Year Header */}
                            <div
                                className={`year-header ${isExpanded ? 'expanded' : ''} ${!yearData.hasActivities ? 'empty' : ''}`}
                                onClick={() => toggleYear(yearData.year)}
                            >
                                <div className="year-header-left">
                                    {isExpanded ? (
                                        <ChevronDown size={20} className="chevron" />
                                    ) : (
                                        <ChevronRight size={20} className="chevron" />
                                    )}
                                    <span className="year-label">{yearData.year}</span>
                                </div>
                                <div className="year-header-right">
                                    {yearData.hasActivities ? (
                                        <span className="year-cost">
                                            {yearData.totalCost.toLocaleString('sv-SE')} kr
                                        </span>
                                    ) : (
                                        <span className="no-activities-label">Inga aktiviteter</span>
                                    )}
                                </div>
                            </div>

                            {/* Year Content - Only render when expanded */}
                            {isExpanded && (
                                <div className="year-content">
                                    {yearData.hasActivities ? (
                                        yearData.baseTypeGroups.map((group) => (
                                            <div
                                                key={group.baseType}
                                                className={`basetype-group ${
                                                    highlightedGroup?.year === yearData.year && highlightedGroup.baseTypes.includes(group.baseType)
                                                        ? 'highlighted' : ''
                                                }`}
                                            >
                                                <div className="basetype-header">
                                                    <span className="basetype-label">{group.label}</span>
                                                    <span className="basetype-cost">
                                                        {group.totalCost.toLocaleString('sv-SE')} kr
                                                    </span>
                                                </div>
                                                <div className="activities-list">
                                                    {group.activities.map((activity, idx) => (
                                                        <div
                                                            key={`${activity.componentKey}-${idx}`}
                                                            className={`activity-row ${selectedComponentKey === activity.componentKey ? 'selected' : ''}`}
                                                            // Without the sidebar the whole row is the tap target
                                                            onClick={sidebarHidden ? () => handleComponentClick(activity.componentKey) : undefined}
                                                        >
                                                            <span
                                                                className="component-name clickable"
                                                                onClick={sidebarHidden ? undefined : () => handleComponentClick(activity.componentKey)}
                                                            >
                                                                {activity.componentName}
                                                            </span>
                                                            <span className="activity-name">
                                                                {activity.activityType === 'replacement' ? 'Byte' : activity.activityName}
                                                            </span>
                                                            <span
                                                                className="cost-value"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setHoverTooltip({
                                                                        content: (
                                                                            <>
                                                                                <strong>{COST_SOURCE_LABELS[activity.costSource ?? 'template']}</strong>
                                                                                <div>{activity.costCalculation}</div>
                                                                                {activity.costSource !== 'template' && activity.costComment && (
                                                                                    <div>{activity.costComment}</div>
                                                                                )}
                                                                            </>
                                                                        ),
                                                                        x: rect.right,
                                                                        y: rect.top + rect.height / 2
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setHoverTooltip(null)}
                                                            >
                                                                <CostSourceChip source={activity.costSource} hasAttachments={activity.hasCostAttachments}>
                                                                    {activity.cost.toLocaleString('sv-SE')} kr
                                                                </CostSourceChip>
                                                            </span>
                                                            {activity.comment && (
                                                                <span className="active-activity-comment">
                                                                    {activity.comment}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-year-message">
                                            Inga planerade aktiviteter detta år
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Tooltip Portal */}
            {tooltip && ReactDOM.createPortal(
                <div
                    className="preview-tooltip"
                    style={{
                        position: 'fixed',
                        top: tooltip.y,
                        left: tooltip.x + 12,
                        transform: 'translateY(-50%)'
                    }}
                >
                    {tooltip.content}
                </div>,
                document.body
            )}
            </div>

            {/* Component Detail Sidebar */}
            <div className="maintenance-active-sidebar">
                <ComponentDetailPane
                    selectedComponent={selectedComponent}
                    building={building}
                    buildingKey={buildingKey!}
                    hasComponents={Object.keys(building.model.structure?.components ?? {}).length > 0}
                    onComponentPatched={handleComponentPatched}
                />
            </div>
            </div>

            {/* Where the sidebar is hidden, the selected component opens here */}
            <Modal
                show={sidebarHidden && !!selectedComponent}
                onHide={() => setSelectedComponentKey(null)}
                size="lg"
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>Komponent</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ComponentDetailPane
                        selectedComponent={selectedComponent}
                        building={building}
                        buildingKey={buildingKey!}
                        hasComponents
                        inModal
                        onComponentPatched={handleComponentPatched}
                    />
                </Modal.Body>
            </Modal>

            {/* Create Draft Modal */}
            <CreateDraftModal
                show={showCreateModal}
                hasActivePlan
                creating={creatingDraft}
                error={createDraftError}
                onConfirm={handleConfirmCreateDraft}
                onCancel={handleCancelCreateDraft}
            />

            {/* Archive Plan Modal */}
            {showArchiveModal && (
                <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Arkivera underhållsplan</h5>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Detta arkiverar den aktiva underhållsplanen utan att ersätta med en ny. Vill du fortsätta?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCancelArchive}>
                                    Avbryt
                                </button>
                                <button type="button" className="btn btn-danger" onClick={handleConfirmArchive}>
                                    Arkivera
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MaintenanceActive;